/* eslint-disable jsx-a11y/control-has-associated-label */
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useQuery } from '@tanstack/react-query';
import { CmdLoader } from '@commander-services/gui-components';
import AddWaypointForm from './AddWaypointForm';
import EditWaypointForm from './EditWaypointForm';
import FilterWaypointsForm from './FilterWaypointsForm';
import WaypointDetailForm from './WaypointDetailForm';
import WaypointForm from './WaypointForm';
import { IDetailWaypointResponseData, IWaypointPostion } from './types';
import CopyWaypointForm from './CopyWaypointForm';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import { getWaypointList } from './WaypointService';
import { WAYPOINT_QUERY_KEY } from './constants';
import {
  activeFilterAtom,
  centerWaypointAtom,
  coordinatesAtom,
  detailWaypointAtom,
  isCopyModeWpAtom,
  isDrawWaypointAtom,
  isEditModeAtom,
  isMinimalizedWpAtom,
  showWaypointLoaderAtom,
  waypointDataAtom,
  waypointIdAtom,
  waypointsAtom,
} from './WaypointState';
import useAnalytics from '../../../hooks/useAnalytics';

export default function Waypoints() {
  const { formatMessage: f } = useIntl();
  const [isOpenedFilter, setIsOpenedFilter] = React.useState(false);
  const [isOpenedDetail, setIsOpenedDetail] = React.useState(false);
  const [isOpenedAdd, setIsOpenedAdd] = React.useState(false);
  const [isOpenedEdit, setIsOpenedEdit] = React.useState(false);
  const detailWaypoint = useRecoilValue<IDetailWaypointResponseData | null>(detailWaypointAtom);
  const [isCopyModeWp, setIsCopyModeWp] = useRecoilState<boolean>(isCopyModeWpAtom);
  const setIsEditMode = useSetRecoilState<boolean>(isEditModeAtom);
  const setIsDrawWaypoint = useSetRecoilState<boolean>(isDrawWaypointAtom);
  const [waypointId, setWaypointId] = useRecoilState<number | null>(waypointIdAtom);
  const setWaypointData = useSetRecoilState<IDetailWaypointResponseData | null>(waypointDataAtom);
  const setCoordinates = useSetRecoilState<IWaypointPostion[] | null>(coordinatesAtom);
  const setCenter = useSetRecoilState<IWaypointPostion | null>(centerWaypointAtom);
  const [waypointDetailId, setWaypointDetailId] = React.useState<number | null>(null);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const [isMinimalizedWp, setIsMinimalized] = useRecoilState<boolean>(isMinimalizedWpAtom);
  const showWaypointLoader = useRecoilValue(showWaypointLoaderAtom);
  const setWaypoints = useSetRecoilState(waypointsAtom);
  const [activeFilter, setActiveFilter] = useRecoilState(activeFilterAtom);
  const { trackEvent } = useAnalytics();

  const handleFilterOptions = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    setIsOpenedFilter(true);
  };

  const handleCloseFilterOptions = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    setIsOpenedFilter(false);
  };

  const handleSaveFilter = (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (event) event.preventDefault();
    setIsOpenedFilter(false);
  };

  const handleOpenDetail = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['map', 'click', 'Show waypoint detail']);

    if (typeof waypointId === 'number') {
      // const findWaypoint = waypoints.find(
      //   (detailWaypointForm) => detailWaypointForm.id === waypointId
      // );

      if (detailWaypoint) {
        setWaypointData(detailWaypoint);
        setWaypointDetailId(waypointId);
        // setWaypointId(waypointId);
      }
      setIsOpenedDetail(true);
    }
  };

  const handleCloseDetail = (event?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (event) event.preventDefault();
    trackEvent(['map', 'click', 'Close waypoint detail']);
    setIsOpenedDetail(false);
  };

  const handleAddWaypointOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['map', 'click', 'Add waypoint']);

    setWaypointData(null);
    setIsDrawWaypoint(true);
    setCoordinates(null);
    setCenter(null);

    setIsOpenedAdd(true);
  };

  const handleCloseAddWaypoint = (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (event) event.preventDefault();
    trackEvent(['map', 'click', 'Close add waypoint']);

    setCoordinates(null);
    setCenter(null);
    setIsDrawWaypoint(false);

    setIsOpenedAdd(false);
  };

  const handleEditWaypointOpen = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['map', 'click', 'Open edit waypoint']);

    if (detailWaypoint && typeof waypointId === 'number') {
      setWaypointData(detailWaypoint);
      // setWaypointId(selectedWaypointId);
      setIsEditMode(true);
      setCoordinates(null);
      setCenter(null);
    }

    setIsOpenedEdit(true);
  };

  const handleCloseEditWaypoint = (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (event) event.preventDefault();
    trackEvent(['map', 'click', 'Close edit waypoint']);
    setIsEditMode(false);
    setIsOpenedEdit(false);
    setIsCopyModeWp(false);
  };

  const handleCopyWaypointOpen = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    setIsCopyModeWp(true);
    trackEvent(['map', 'click', 'Open copy waypoint']);

    if (detailWaypoint && typeof waypointId === 'number') {
      setWaypointData(detailWaypoint);
      // setWaypointId(selectedWaypointId);
      setIsEditMode(true);
      setCoordinates(null);
      setCenter(null);
    }
  };

  const handleCloseCopyWaypoint = (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (event) event.preventDefault();
    trackEvent(['map', 'click', 'Close copy waypoint']);
    setIsEditMode(false);

    setIsCopyModeWp(false);
  };

  const handleSaveEditWaypoint = () => {
    setIsOpenedEdit(false);
    trackEvent(['map', 'click', 'Save edit waypoint']);
  };
  // const handleSaveCopyWaypoint = () => {

  //   setIsCopyModeWp(false);
  // };

  const handleSaveAddWaypoint = () => {
    trackEvent(['map', 'click', 'Save add waypoint']);
    setCoordinates(null);
    setCenter(null);
    setIsDrawWaypoint(false);
    setIsOpenedAdd(false);
  };

  const handleMinimalizeWP = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    trackEvent(['map', 'click', 'Minimalize waypoints list']);
    setIsMinimalized(true);
  };

  const loadWaypoints = async () => {
    setIsCopyModeWp(false);

    // LoaderService.showLoader();

    const data = await getWaypointList(activeFilter ? { filters: { ...activeFilter } } : null);
    if (data) {
      // LoaderService.showLoader(false);

      setWaypoints(data.waypoints);
      setActiveFilter(data.filters ? { ...data.filters } : null);
    }
  };

  const { isLoading, data } = useQuery({
    queryKey: [WAYPOINT_QUERY_KEY, selectedCustomers, activeFilter],
    queryFn: () => loadWaypoints(),
    refetchOnWindowFocus: true,
    refetchOnReconnect: false,
  });

  if (isMinimalizedWp) {
    return null;
  }

  return (
    <div className="w-mc-waypoints d-none d-lg-block">
      {!showWaypointLoader && (
        <div className="b-mc-waypoints-tabs">
          <a href="#" className="item active">
            {f({ id: 'waypoints.tab.waypoints' })}
          </a>
          {/* <a href="#" className="item">
          Skupiny WP
        </a> */}
          <a
            href="#"
            title={f({ id: 'waypoints.tab.minimalize' })}
            className="item item--collapse"
            onClick={handleMinimalizeWP}
          >
            <i className="cmd-icons cmd-icon-dropdown" />
          </a>
        </div>
      )}
      {isLoading && <CmdLoader inContent={true} />}

      {!isOpenedFilter && !isOpenedDetail && !isOpenedAdd && !isOpenedEdit && !isCopyModeWp && (
        <WaypointForm
          filterWaypointOpen={handleFilterOptions}
          detailWaypointOpen={handleOpenDetail}
          addWaypointOpen={handleAddWaypointOpen}
          editWaypointOpen={handleEditWaypointOpen}
          copyWaypointOpen={handleCopyWaypointOpen}
        />
      )}
      {isOpenedFilter && (
        <FilterWaypointsForm
          closeFilterOptions={handleCloseFilterOptions}
          saveFilterWaypoint={handleSaveFilter}
        />
      )}
      {isOpenedDetail && (
        <WaypointDetailForm closeDetail={handleCloseDetail} id={waypointDetailId} />
      )}
      {isOpenedAdd && (
        <AddWaypointForm
          closeAddWaypoint={handleCloseAddWaypoint}
          saveAddWaypoint={handleSaveAddWaypoint}
        />
      )}
      {isOpenedEdit && !isCopyModeWp && (
        <EditWaypointForm
          closeEditWaypoint={handleCloseEditWaypoint}
          saveEditWaypoint={handleSaveEditWaypoint}
        />
      )}
      {isCopyModeWp && (
        <CopyWaypointForm
          closeCopyWaypoint={handleCloseCopyWaypoint}
          // saveCopyWaypoint={handleSaveCopyWaypoint}
        />
      )}
    </div>
  );
}
